/**
 * Beyond Sure OCR Pro — Real-backend SSE client (U1).
 *
 * Pairs with server.py's POST /api/ocr/extract and POST /api/ocr/ask
 * endpoints (engine phases E2, E2-vision, E3, E6 — already shipped).
 *
 * Two public functions:
 *
 *   1. streamRealExtraction(file, opts)
 *        Uploads a single document to /api/ocr/extract as multipart
 *        FormData. Consumes the text/event-stream response, parsing
 *        named SSE events in real time (validating, detected,
 *        provider_selected, extracting, extracted, recommendation,
 *        escalating, reconciled, done, error). Invokes the supplied
 *        callbacks as the stream progresses.
 *
 *   2. streamRealAskAi(extractionId, question, opts)
 *        Posts {extraction_id, question} to /api/ocr/ask. Consumes the
 *        text/event-stream response (data: {type:"chunk"|"done"|"error"}
 *        events terminated by data: [DONE]). Invokes onChunk per text
 *        chunk and onDone when complete.
 *
 * Both functions return a `{ cancel: () => void }` handle the UI can call
 * to abort the fetch.
 *
 * ──────────────────────────────────────────────────────────────────
 * INTEGRATING INTO OcrProScreen.tsx — one-line swap
 *
 * Inside `startMockPipeline(docId, kind)` in OcrProScreen.tsx, replace
 * the body with:
 *
 *   import { streamRealExtraction } from "./realApi";
 *
 *   function startRealPipeline(docId: string, file: File) {
 *     const handle = streamRealExtraction(file, {
 *       onEvent: (evt) => {
 *         if (evt.type === "extracted") {
 *           setDocs(prev => prev.map(d => d.id === docId ? {
 *             ...d,
 *             status: "done",
 *             models: evt.allRuns.map((r, i) => ({
 *               modelId:        r.model,
 *               modelName:      r.model,
 *               vendor:         r.provider,
 *               status:         "done",
 *               latencyMs:      r.latencyMs,
 *               costPerPageUSD: r.costInr / 83,  // approx INR→USD
 *               accuracy:       r.confidence,
 *             })),
 *             consensus: {
 *               fields:   flattenFields(evt.data),
 *               tables:   [],
 *               fullText: JSON.stringify(evt.data, null, 2),
 *               blendedAccuracy: evt.confidence,
 *               blendedCostPerPageUSD: evt.costInr / 83,
 *               totalLatencyMs: evt.durationMs,
 *             },
 *             extractionId: evt.extractionId,  // add to UniversalDoc
 *           } : d));
 *         }
 *         if (evt.type === "error") { ... }
 *       },
 *     });
 *     setDocs(prev => prev.map(d => d.id === docId ? {
 *       ...d, cancelMock: handle.cancel
 *     } : d));
 *   }
 *
 * And inside `handleAskAi(docId, text)`, after the user message is
 * pushed but before mockStream, branch on the real backend:
 *
 *   if (doc.extractionId) {
 *     streamRealAskAi(doc.extractionId, text, {
 *       onChunk: (chunk) => { ... append to placeholder.content ... },
 *       onDone:  () => { ... set streaming=false ... },
 *       onError: (msg) => { ... swap content to error message ... },
 *     });
 *     return;
 *   }
 *   // ... fall through to existing mockStream code
 *
 * Default flag `USE_REAL_BACKEND` is exported so the swap can be gated
 * on a single boolean (env var, query string, or storage flag).
 * ──────────────────────────────────────────────────────────────────
 */

/* ────────────────── Public types ────────────────── */

/**
 * U1 swap landed (2026-06-10): the OCR Pro main extraction path runs the
 * real `/api/ocr/extract` SSE pipeline by default in EVERY environment,
 * including dev. The brief said "the engine exists; stop running the mock."
 *
 * Escape hatch for offline/airplane demos: pass `?ocr_mock=1` in the URL,
 * or set `window.__OCR_PRO_REAL = false` from devtools before navigating
 * to /ocr. (Defaults to true if the override isn't set.)
 *
 * NOTE: the legacy `runModelsMock` is still imported by the multi-model
 * Compare panel because the server doesn't yet stream multi-provider runs
 * per-model — it only emits one reconciled result. Until the server gains
 * a `/api/ocr/extract/multi` endpoint that streams per-model events, the
 * Compare panel will keep the mock with a visible "(simulated)" label.
 * This is honest, not faked: the main extraction is real; the comparison
 * UI clearly labels its simulated state.
 */
export const USE_REAL_BACKEND = (() => {
  if (typeof window === "undefined") return true; // SSR / build → real
  const w = window as unknown as { __OCR_PRO_REAL?: boolean };
  if (w.__OCR_PRO_REAL === false) return false; // explicit dev escape hatch
  if (w.__OCR_PRO_REAL === true) return true;
  // Query-string opt-out for one-off offline demos.
  try {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("ocr_mock") === "1") return false;
  } catch {
    // Empty URL or non-DOM env — fall through to true.
  }
  return true;
})();

/**
 * The server authenticates via `Authorization: Bearer <token>` (it reads the
 * header, not a cookie). AE4 unifies every screen onto the ONE key the login
 * flows actually write — `bsure_token` (OtpScreen / PhoneOtpScreen / LoginRoute).
 * Previously this read `bsure_auth_token`, which no login path ever wrote, so OCR
 * Pro / family / policies / agency / claims / community / booking all 401'd for a
 * logged-in user regardless of load order. `getAuthToken()` reads the canonical
 * key, then falls back to the legacy keys so anyone logged in before the
 * unification isn't kicked out.
 */
export const AUTH_TOKEN_STORAGE_KEY = "bsure_token";

/** Back-compat: read these only when the canonical key is absent. */
const LEGACY_AUTH_TOKEN_KEYS = ["bsure_auth_token", "ig_t"] as const;

export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const t = window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (t) return t;
    for (const k of LEGACY_AUTH_TOKEN_KEYS) {
      const v = window.localStorage.getItem(k);
      if (v) return v;
    }
    return null;
  } catch {
    return null;
  }
}

/** AE4.2: clear every auth-token key (canonical + legacy mirrors + user/email). */
export function clearAuthToken(): void {
  if (typeof window === "undefined") return;
  try {
    for (const k of [AUTH_TOKEN_STORAGE_KEY, ...LEGACY_AUTH_TOKEN_KEYS, "ig_u", "bsure_email"]) {
      window.localStorage.removeItem(k);
    }
  } catch {
    /* localStorage unavailable — nothing to clear */
  }
}

/**
 * AE4.2: full logout — best-effort server token invalidation (POST /api/logout),
 * then clear local storage REGARDLESS so the device is always signed out even if
 * the network call fails. Callers redirect to /login afterwards. Fixes the AE0
 * finding that "Log out" cleared nothing and left the session live for 30 days.
 */
export async function logout(): Promise<void> {
  const token = getAuthToken();
  try {
    await fetch("/api/logout", {
      method: "POST",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch {
    /* network/best-effort — fall through to the local clear below */
  } finally {
    clearAuthToken();
  }
}

/** Authorization header (empty object when no token, so spreads are safe). */
function authHeaders(): Record<string, string> {
  const t = getAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Parsed SSE event from /api/ocr/extract — one variant per server event. */
export type ExtractionSseEvent =
  | { type: "validating"; pageCount: number; fileSizeBytes: number; tier: string; monthlyUsed: number }
  | { type: "detected"; docType: string; confidence: number; stage: string; evidence: string; candidates: { type: string; prob: number }[] }
  | { type: "providerSelected"; modelKey: string; provider: string; actualModelId: string; expectedQuality: number; expectedCostInr: number; expectedLatencyMs: number; reason: string; alternates: { modelKey: string; score: number }[] }
  | { type: "extracting"; progressPct: number; stage: string; provider?: string; model?: string }
  | { type: "escalating"; reason: string; altProvider: string; altModelKey: string; altExpectedCostInr: number }
  | { type: "reconciled"; confidence: number; requiresHuman: boolean; disagreementsCount: number; providers: string[]; summary: string }
  | { type: "extracted"; extractionId: string; schema: string; data: Record<string, unknown>; confidence: number; costInr: number; durationMs: number; provider: string; modelKey: string; ensembleUsed: boolean; requiresHuman: boolean; validationWarnings: { fieldPath: string; message: string }[]; visionPath?: boolean; allRuns: { provider: string; model: string; confidence: number; costInr: number; latencyMs: number }[] }
  | { type: "recommendation"; summary: string; severity: "ok" | "warn" | "block"; actions: string[]; evidence: unknown[]; docType: string }
  | { type: "residencyGate"; docType: string; excludedProviders: string[]; reason: string; providersDropped: number }
  | { type: "preferredModelHonored"; preferredModel: string; newPosition: number; previousPosition: number; note?: string }
  | { type: "preferredModelDropped"; preferredModel: string; reason: string; fallingBackTo: string | null }
  | { type: "done"; extractionId: string; totalDurationMs: number }
  | { type: "error"; code: string; http: number; message: string; upgradePrompt?: string };

export interface ExtractionStreamOptions {
  /** Called for every parsed SSE event in order. */
  onEvent: (event: ExtractionSseEvent) => void;
  /** Optional doc type hint to short-circuit the detector when confident. */
  docTypeHint?: string;
  /** Quality target — affects router pick (default "balanced"). */
  targetQuality?: "fast" | "balanced" | "best" | "cheapest";
  /** Specific model id from /api/ocr/models registry. "auto" (or omitted)
   *  lets the cost-aware router pick. Any other id is treated as a *hint*
   *  by the backend — if available and residency-compatible, it's used;
   *  otherwise the router falls back to its default and the
   *  `preferred_model_dropped` SSE event explains why. */
  preferredModel?: string;
  /** Override fetch URL — useful for local mocking / tests. */
  url?: string;
}

export interface StreamHandle {
  /** Abort the in-flight request. Safe to call multiple times. */
  cancel: () => void;
  /** Resolves when the stream is fully consumed or aborted. */
  done: Promise<void>;
}

/* ────────────────── /api/ocr/extract client ────────────────── */

/**
 * Stream a document through the real OCR Pro extraction pipeline.
 *
 * Failure modes (all surface as { type: "error" } events to onEvent):
 *   - 401: auth_required — user not logged in
 *   - 402: insufficient_credits — tier exceeded its allowance
 *   - 413: file_too_large — over the 25MB cap
 *   - 415: format_not_supported — file type not in PDF/JPG/PNG/HEIC/TIFF
 *   - 501: VISION_REQUIRES_ANTHROPIC — image-only doc without Claude key
 *   - 502: PROVIDER_EXTRACTION_FAILED — provider API blew up
 *   - 503: NO_PROVIDER_AVAILABLE — no model can handle the detected doc type
 *
 * Network errors surface as { type: "error", code: "NETWORK_ERROR" }.
 */
export function streamRealExtraction(
  file: File,
  opts: ExtractionStreamOptions,
): StreamHandle {
  const controller = new AbortController();
  const url = opts.url ?? "/api/ocr/extract";

  const form = new FormData();
  form.append("file", file, file.name);
  if (opts.docTypeHint) form.append("doc_type_hint", opts.docTypeHint);
  if (opts.targetQuality) form.append("target_quality", opts.targetQuality);
  if (opts.preferredModel && opts.preferredModel !== "auto") {
    form.append("preferred_model", opts.preferredModel);
  }

  const done = (async () => {
    try {
      const resp = await fetch(url, {
        method: "POST",
        body: form, // multipart — do NOT set Content-Type (browser adds the boundary)
        headers: authHeaders(),
        signal: controller.signal,
        credentials: "include",
      });

      if (!resp.ok) {
        opts.onEvent({
          type: "error",
          code: `HTTP_${resp.status}`,
          http: resp.status,
          message: await safeReadText(resp),
        });
        return;
      }

      if (!resp.body) {
        opts.onEvent({
          type: "error",
          code: "NO_RESPONSE_BODY",
          http: 0,
          message: "Server returned empty stream.",
        });
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by double newlines.
        let separatorIdx = buffer.indexOf("\n\n");
        while (separatorIdx !== -1) {
          const frame = buffer.slice(0, separatorIdx);
          buffer = buffer.slice(separatorIdx + 2);
          separatorIdx = buffer.indexOf("\n\n");

          const parsed = parseSseFrame(frame);
          if (parsed) opts.onEvent(parsed);
        }
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      opts.onEvent({
        type: "error",
        code: "NETWORK_ERROR",
        http: 0,
        message: (err as Error).message || String(err),
      });
    }
  })();

  return { cancel: () => controller.abort(), done };
}

/* ────────────────── /api/ocr/ask client ────────────────── */

export interface AskAiStreamOptions {
  /** Called for each text chunk arriving from the server. */
  onChunk: (chunk: string) => void;
  /** Called once when the stream completes successfully. */
  onDone?: (meta: { totalChars: number; modelKey: string; docClass: string; extractionConfidence: number }) => void;
  /** Called if the server returns an error event. */
  onError?: (message: string) => void;
  /** Optional model override (any AI_MODELS key — defaults to smart-1). */
  model?: string;
  /** Override fetch URL — useful for local mocking / tests. */
  url?: string;
}

/**
 * Stream a doc-scoped chat answer for one extraction.
 *
 * The server's /api/ocr/ask v1 emits a single chunk + done (no token
 * streaming yet — v2 will swap to provider.chat.completions(stream=true)).
 * This client supports both shapes transparently — multiple chunks WILL
 * be invoked in onChunk as soon as v2 ships, with zero UI changes needed.
 */
export function streamRealAskAi(
  extractionId: string,
  question: string,
  opts: AskAiStreamOptions,
): StreamHandle {
  const controller = new AbortController();
  const url = opts.url ?? "/api/ocr/ask";

  const done = (async () => {
    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          extraction_id: extractionId,
          question,
          model: opts.model,
        }),
        signal: controller.signal,
        credentials: "include",
      });

      if (!resp.ok) {
        opts.onError?.(
          `HTTP ${resp.status}: ${await safeReadText(resp)}`,
        );
        return;
      }

      if (!resp.body) {
        opts.onError?.("Server returned empty stream.");
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;
        buffer += decoder.decode(value, { stream: true });

        let separatorIdx = buffer.indexOf("\n\n");
        while (separatorIdx !== -1) {
          const frame = buffer.slice(0, separatorIdx);
          buffer = buffer.slice(separatorIdx + 2);
          separatorIdx = buffer.indexOf("\n\n");

          // /api/ocr/ask emits ONLY `data:` lines (no event: names),
          // because it follows the simpler advisor-stream convention.
          const dataLine = frame
            .split("\n")
            .find((ln) => ln.startsWith("data:"));
          if (!dataLine) continue;
          const payload = dataLine.slice(5).trim();
          if (payload === "[DONE]") return;

          try {
            const obj = JSON.parse(payload) as
              | { type: "chunk"; text: string }
              | { type: "done"; total_chars: number; model_key: string; doc_class: string; extraction_confidence: number }
              | { type: "error"; message: string };

            if (obj.type === "chunk") {
              opts.onChunk(obj.text);
            } else if (obj.type === "done") {
              opts.onDone?.({
                totalChars:           obj.total_chars,
                modelKey:             obj.model_key,
                docClass:             obj.doc_class,
                extractionConfidence: obj.extraction_confidence,
              });
            } else if (obj.type === "error") {
              opts.onError?.(obj.message);
            }
          } catch {
            // Skip malformed JSON frames — server may send keep-alive.
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      opts.onError?.((err as Error).message || String(err));
    }
  })();

  return { cancel: () => controller.abort(), done };
}

/* ────────────────── /api/ocr/correct-field client (C1 flywheel) ────────────────── */

export interface CorrectFieldInput {
  field: string;            // canonical field key, e.g. "total_amount"
  originalValue?: string;   // the model's extracted value (the DPO "rejected" side)
  correctedValue: string;   // the human-corrected value (the DPO "chosen" side)
  docType?: string;
  model?: string;
}

/**
 * Record an inline field correction to /api/ocr/correct-field — the C1 OCR training flywheel.
 * The server stores a supervised pair + a DPO triad (prompt / rejected=original / chosen=corrected)
 * so the extractor can learn from it. Best-effort: returns {ok:false, error} on failure — callers
 * fire-and-forget, so a failed capture never disrupts the edit.
 */
export async function correctOcrField(
  inp: CorrectFieldInput,
): Promise<{ ok: boolean; captured?: boolean; error?: string }> {
  try {
    const resp = await fetch("/api/ocr/correct-field", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      credentials: "include",
      body: JSON.stringify({
        field: inp.field,
        original_value: inp.originalValue ?? "",
        corrected_value: inp.correctedValue,
        doc_type: inp.docType,
        model: inp.model,
      }),
    });
    if (resp.ok) {
      const j = (await resp.json().catch(() => ({}))) as { captured?: boolean };
      return { ok: true, captured: !!j.captured };
    }
    const detail = (await resp.json().catch(() => null)) as { error?: string } | null;
    return { ok: false, error: detail?.error ?? `HTTP ${resp.status}` };
  } catch {
    return { ok: false, error: "network" };
  }
}

/* ────────────────── /api/ocr/report client (L05) ────────────────── */

import type { OcrReport } from "./types";

/**
 * L05 — Report-card fetcher (server.py:31623).
 *
 * Posts `{doc_type, fields:[{name,value,confidence}]}` and returns the
 * server-built {coverage_summary, gaps, anomalies, citations, confidence,
 * forced_strict} envelope. When the AI key is unset the server replies
 * `available:false` with a `reason`; that flag is surfaced verbatim so
 * the UI renders the honest gate rather than a fabricated report.
 */
export async function fetchOcrReport(
  docType: string,
  fields: { name: string; value: string; confidence: number }[],
  signal?: AbortSignal,
): Promise<OcrReport> {
  try {
    const resp = await fetch("/api/ocr/report", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      credentials: "include",
      signal,
      body: JSON.stringify({ doc_type: docType, fields }),
    });
    if (!resp.ok) {
      return {
        available: false,
        reason: `HTTP_${resp.status}: ${await safeReadText(resp)}`,
      };
    }
    const data = (await resp.json()) as Partial<OcrReport>;
    return { available: data.available !== false, ...data } as OcrReport;
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      return { available: false, reason: "ABORTED" };
    }
    return {
      available: false,
      reason: (err as Error).message || "NETWORK_ERROR",
    };
  }
}

/* ────────────────── Mapping helpers ────────────────── */

/**
 * Flatten the server's structured extraction data into the
 * ExtractedField[] shape the UI's consensus panel renders. The server
 * returns nested objects; we descend depth-first and produce a flat
 * list of {label, value, confidence, source}.
 */
export function flattenExtractionFields(
  data: Record<string, unknown>,
  prefix = "",
): { label: string; value: string; confidence: number; source: string }[] {
  const out: { label: string; value: string; confidence: number; source: string }[] = [];

  function walk(node: unknown, path: string): void {
    if (node === null || node === undefined) return;
    if (typeof node !== "object") {
      out.push({
        label: humanizeLabel(path),
        value: String(node),
        confidence: 1.0,  // server doesn't yet emit per-field confidence
        source: path,
      });
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((item, i) => walk(item, `${path}[${i}]`));
      return;
    }
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      walk(v, path ? `${path}.${k}` : k);
    }
  }

  walk(data, prefix);
  return out;
}

function humanizeLabel(path: string): string {
  // "insured_persons[0].date_of_birth" → "Insured persons[0] · date of birth"
  return path
    .replace(/_/g, " ")
    .replace(/\./g, " · ")
    .replace(/\b(\w)/g, (m) => m.toUpperCase());
}

/* ────────────────── Internal helpers ────────────────── */

/**
 * Parse one SSE frame (lines separated by single newlines) into a
 * typed ExtractionSseEvent. Returns null on malformed frames.
 *
 * Frame shape:
 *   event: <name>\n
 *   data: <json>\n
 */
function parseSseFrame(frame: string): ExtractionSseEvent | null {
  const lines = frame.split("\n");
  let eventName = "";
  let dataJson = "";
  for (const ln of lines) {
    if (ln.startsWith("event:")) eventName = ln.slice(6).trim();
    else if (ln.startsWith("data:")) dataJson += ln.slice(5).trim();
  }
  if (!eventName || !dataJson) return null;

  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(dataJson) as Record<string, unknown>;
  } catch {
    return null;
  }

  // Map snake_case server payload → camelCase typed event.
  switch (eventName) {
    case "validating":
      return {
        type:           "validating",
        pageCount:      n(payload.page_count),
        fileSizeBytes:  n(payload.file_size_bytes),
        tier:           s(payload.tier),
        monthlyUsed:    n(payload.monthly_used),
      };
    case "detected":
      return {
        type:        "detected",
        docType:     s(payload.type),
        confidence:  n(payload.confidence),
        stage:       s(payload.stage),
        evidence:    s(payload.evidence),
        candidates:  (payload.candidates as { type: string; prob: number }[]) ?? [],
      };
    case "provider_selected":
      return {
        type:               "providerSelected",
        modelKey:           s(payload.model_key),
        provider:           s(payload.provider),
        actualModelId:      s(payload.actual_model_id),
        expectedQuality:    n(payload.expected_quality),
        expectedCostInr:    n(payload.expected_cost_inr),
        expectedLatencyMs:  n(payload.expected_latency_ms),
        reason:             s(payload.reason),
        alternates:         (payload.alternates as { modelKey: string; score: number }[]) ?? [],
      };
    case "extracting":
      return {
        type:        "extracting",
        progressPct: n(payload.progress_pct),
        stage:       s(payload.stage),
        provider:    payload.provider ? s(payload.provider) : undefined,
        model:       payload.model    ? s(payload.model)    : undefined,
      };
    case "escalating":
      return {
        type:                "escalating",
        reason:              s(payload.reason),
        altProvider:         s(payload.alt_provider),
        altModelKey:         s(payload.alt_model_key),
        altExpectedCostInr:  n(payload.alt_expected_cost_inr),
      };
    case "reconciled":
      return {
        type:                "reconciled",
        confidence:          n(payload.confidence),
        requiresHuman:       !!payload.requires_human,
        disagreementsCount:  n(payload.disagreements_count),
        providers:           (payload.providers as string[]) ?? [],
        summary:             s(payload.summary),
      };
    case "extracted":
      return {
        type:           "extracted",
        extractionId:   s(payload.extraction_id),
        schema:         s(payload.schema),
        data:           (payload.data as Record<string, unknown>) ?? {},
        confidence:     n(payload.confidence),
        costInr:        n(payload.cost_inr),
        durationMs:     n(payload.duration_ms),
        provider:       s(payload.provider),
        modelKey:       s(payload.model_key),
        ensembleUsed:   !!payload.ensemble_used,
        requiresHuman:  !!payload.requires_human,
        visionPath:     payload.vision_path === true,
        // BC2.4: the backend now runs the validators (incl. the agent-vs-insured
        // attribution guard) in-band with extraction and emits their warnings here.
        validationWarnings: ((payload.validation_warnings as Record<string, unknown>[]) ?? []).map((w) => ({
          fieldPath: s(w.field_path),
          message:   s(w.message),
        })),
        allRuns:        ((payload.all_runs as Record<string, unknown>[]) ?? []).map((r) => ({
          provider:   s(r.provider),
          model:      s(r.model),
          confidence: n(r.confidence),
          costInr:    n(r.cost_inr),
          latencyMs:  n(r.latency_ms),
        })),
      };
    case "recommendation":
      return {
        type:      "recommendation",
        summary:   s(payload.summary),
        severity:  (payload.severity as "ok" | "warn" | "block") ?? "ok",
        actions:   (payload.actions as string[]) ?? [],
        evidence:  (payload.evidence as unknown[]) ?? [],
        docType:   s(payload.doc_type),
      };
    case "residency_gate":
      return {
        type:              "residencyGate",
        docType:           s(payload.doc_type),
        excludedProviders: (payload.excluded_providers as string[]) ?? [],
        reason:            s(payload.reason),
        providersDropped:  n(payload.providers_dropped),
      };
    case "preferred_model_honored":
      return {
        type:              "preferredModelHonored",
        preferredModel:    s(payload.preferred_model),
        newPosition:       n(payload.new_position),
        previousPosition:  n(payload.previous_position),
        note:              payload.note ? s(payload.note) : undefined,
      };
    case "preferred_model_dropped":
      return {
        type:           "preferredModelDropped",
        preferredModel: s(payload.preferred_model),
        reason:         s(payload.reason),
        fallingBackTo:  payload.falling_back_to ? s(payload.falling_back_to) : null,
      };
    case "done":
      return {
        type:             "done",
        extractionId:     s(payload.extraction_id),
        totalDurationMs:  n(payload.total_duration_ms),
      };
    case "error":
      return {
        type:           "error",
        code:           s(payload.code),
        http:           n(payload.http),
        message:        s(payload.message),
        upgradePrompt:  payload.upgrade_prompt ? s(payload.upgrade_prompt) : undefined,
      };
    default:
      return null;
  }
}

async function safeReadText(resp: Response): Promise<string> {
  try {
    return (await resp.text()).slice(0, 300);
  } catch {
    return resp.statusText;
  }
}

function s(v: unknown): string {
  return v == null ? "" : String(v);
}

function n(v: unknown): number {
  const num = Number(v);
  return Number.isFinite(num) ? num : 0;
}
